import React, { useMemo } from 'react';
import type { Geometry } from 'geojson';
import Map, { Layer, ScaleControl, Source } from 'react-map-gl';
import type { LayerProps } from 'react-map-gl';
import { Box, Typography } from '@mui/material';

import {
  BASE_MAPBOX_STYLE_URL,
  DEFAULT_INITIAL_COORDS,
  MAPBOX_ACCESS_TOKEN,
} from '../../consts';
import { getFieldSessionTrackBounds } from './bounds';
import { FormattedMessage } from 'react-intl';

export { getFieldSessionTrackBounds } from './bounds';

const trackLayer: LayerProps = {
  id: 'field-session-track',
  type: 'line',
  paint: {
    'line-color': '#ff7a00',
    'line-width': 4,
    'line-opacity': 0.95,
  },
};

const mapStyles = {
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  bgcolor: 'sunken',
};
const compactMapStyles = {
  ...mapStyles,
  '& .mapboxgl-ctrl-bottom-left .mapboxgl-ctrl': {
    margin: '0 0 5px 5px',
  },
  '& .mapboxgl-ctrl-scale': {
    height: 13,
    px: 0.5,
    fontSize: 9,
    lineHeight: '11px',
  },
};
const emptyMapStyles = {
  ...mapStyles,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

interface FieldSessionMapProps {
  track?: Geometry | null;
  borderRadius?: number;
  compact?: boolean;
}

export const FieldSessionMap = React.memo(
  ({ track, borderRadius = 1.5, compact = false }: FieldSessionMapProps) => {
    const bounds = useMemo(() => getFieldSessionTrackBounds(track), [track]);
    const initialViewState = useMemo(
      () =>
        bounds
          ? {
              bounds,
              fitBoundsOptions: { padding: 24, maxZoom: 17 },
            }
          : DEFAULT_INITIAL_COORDS,
      [bounds]
    );
    const trackFeature = useMemo(
      () =>
        track
          ? ({ type: 'Feature', properties: {}, geometry: track } as const)
          : null,
      [track]
    );

    if (!trackFeature) {
      return (
        <Box sx={emptyMapStyles} borderRadius={borderRadius}>
          <Typography variant="body2" color="text.secondary">
            <FormattedMessage
              id="fieldSessions.noTrack"
              defaultMessage="No track recorded"
            />
          </Typography>
        </Box>
      );
    }

    const scalePosition = compact ? 'bottom-left' : 'bottom-right';
    const scaleMaxWidth = compact ? 64 : 100;

    return (
      <Box
        sx={compact ? compactMapStyles : mapStyles}
        borderRadius={borderRadius}
      >
        <Map
          initialViewState={initialViewState}
          mapStyle={BASE_MAPBOX_STYLE_URL}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          logoPosition={compact ? 'bottom-right' : undefined}
          boxZoom={false}
          dragPan={false}
          dragRotate={false}
          doubleClickZoom={false}
          keyboard={false}
          scrollZoom={false}
          touchPitch={false}
          touchZoomRotate={false}
          reuseMaps={!compact}
        >
          <Source type="geojson" data={trackFeature}>
            <Layer {...trackLayer} />
          </Source>
          <ScaleControl
            unit="metric"
            position={scalePosition}
            maxWidth={scaleMaxWidth}
          />
        </Map>
      </Box>
    );
  }
);
